import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiChevronLeft,
  FiChevronRight,
  FiUsers,
  FiCalendar,
  FiArrowRight,
  FiBookOpen,
  FiEdit3,
  FiColumns,
  FiAlignJustify,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { fetchDevotionDay } from "../../store/devotionSlice";
import { fetchUserSettings } from "../../store/settingsSlice";
import PassageReader from "./PassageReader";
import PastoralGuide from "./PastoralGuide";
import DevotionEditor from "./DevotionEditor";
import LoginForm from "../LoginForm";
import { useModal } from "../../context/Modal";
import CompactDatePicker from "./calendar/CompactDatePicker";
import DevotionCalendarPopover from "./calendar/DevotionCalendarPopover";
import { parseDateOnly } from "../../utils/dateOnly";
import { versionForLanguage } from "../../utils/bibleVersions";

const NEU_RAISED_LG =
  "shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]";
const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";

// Lighter drop shadows used only in the mobile header per §4 of the spec.
const MOBILE_CARD_SHADOW =
  "0 12px 28px rgba(42, 68, 88, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.78)";
const MOBILE_BTN_SHADOW = "0 5px 12px rgba(42, 68, 88, 0.16)";
const MOBILE_PRIMARY_SHADOW = "0 10px 22px rgba(229, 116, 51, 0.35)";

const DailyDevotionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { date } = useParams();
  const { i18n } = useTranslation();
  const { openModal } = useModal();
  const user = useSelector((state) => state.session.user);
  const { currentDay, loading, error } = useSelector((state) => state.devotions);
  const { defaultVersion, isLoaded: settingsLoaded } = useSelector((state) => state.settings);
  const language = i18n.language?.startsWith("ko") ? "ko" : "en";
  const [version, setVersion] = useState(() => versionForLanguage(language, defaultVersion));
  const [userOverride, setUserOverride] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });

  const mobileCalendarBtnRef = useRef(null);
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false);
  // Mobile <lg uses tabs to split the long page. Reset to "passage" when the
  // selected day changes so the user starts with scripture on the next day.
  const [mobileTab, setMobileTab] = useState("passage"); // "passage" | "reflect"
  useEffect(() => {
    setMobileTab("passage");
  }, [date]);
  // Desktop layout: user can toggle between read-and-write two-column and a
  // single-column stack. Persisted so the choice sticks between visits.
  const [desktopLayout, setDesktopLayout] = useState(() => {
    try {
      const stored = localStorage.getItem("devotionLayout");
      return stored === "stack" ? "stack" : "split";
    } catch (_e) {
      return "split";
    }
  });
  const updateDesktopLayout = (value) => {
    setDesktopLayout(value);
    try {
      localStorage.setItem("devotionLayout", value);
    } catch (_e) {
      // Ignore storage errors.
    }
  };

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "bibleMemosTheme" && event.newValue) {
        setTheme(event.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (user && !settingsLoaded) {
      dispatch(fetchUserSettings());
    }
  }, [dispatch, user, settingsLoaded]);

  // When the user switches nav language, reset any manual version override so
  // scripture follows the current language.
  useEffect(() => {
    setUserOverride(false);
  }, [language]);

  // Keep the shown version aligned with the current language (and the user's
  // saved default when it belongs to that language). A manual dropdown pick
  // sets userOverride and pauses this sync until the language changes again.
  useEffect(() => {
    if (userOverride) return;
    const next = versionForLanguage(language, defaultVersion);
    setVersion((current) => (current === next ? current : next));
  }, [defaultVersion, language, userOverride]);

  useEffect(() => {
    dispatch(
      fetchDevotionDay({
        date,
        version,
        language,
      })
    );
  }, [date, dispatch, language, version]);

  const changeVersion = (nextVersion) => {
    setUserOverride(true);
    setVersion(nextVersion);
  };

  const navPill = `inline-flex items-center gap-space-xs h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`;

  const currentPassageDate = currentDay?.passage?.passage_date;
  const fullDateLabel = currentPassageDate
    ? parseDateOnly(currentPassageDate).toLocaleDateString(
        language === "ko" ? "ko-KR" : "en-US",
        { dateStyle: "full" }
      )
    : "";
  const shortDateLabel = currentPassageDate
    ? parseDateOnly(currentPassageDate).toLocaleDateString(
        language === "ko" ? "ko-KR" : "en-US",
        { month: "long", day: "numeric" }
      )
    : "";

  const chooseFromMobileCalendar = (selectedDate) => {
    setMobileCalendarOpen(false);
    mobileCalendarBtnRef.current?.focus();
    navigate(`/devotions/date/${selectedDate}`);
  };

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors overflow-x-hidden"
    >
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-4 md:px-margin-neu py-space-lg md:py-space-xl flex flex-col gap-6 md:gap-space-xl">
          {/* ------------------------------------------------------------------
              Header — MOBILE (<=600px)
              Single-column layout per the spec: eyebrow + My Devotions text
              link on top row; big date; 3-col nav row; full-width Sunday CTA.
             ------------------------------------------------------------------ */}
          <section
            className="relative min-[601px]:hidden rounded-[28px] bg-canvas-base flex flex-col"
            style={{ padding: "26px 24px", boxShadow: MOBILE_CARD_SHADOW }}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Daily Devotion
              </span>
              {user ? (
                <Link
                  to="/my-memos/devotions"
                  className="inline-flex items-center gap-1 text-label-md font-semibold text-accent-cta hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-cta focus-visible:outline-offset-2 rounded-sm min-h-[44px] py-2"
                >
                  My Devotions
                  <FiArrowRight aria-hidden="true" />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => openModal(<LoginForm />)}
                  aria-label="Sign in to open My Devotions"
                  className="inline-flex items-center gap-1 text-label-md font-semibold text-accent-cta hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-cta focus-visible:outline-offset-2 rounded-sm min-h-[44px] py-2"
                >
                  My Devotions
                  <FiArrowRight aria-hidden="true" />
                </button>
              )}
            </div>

            {currentPassageDate && (
              <h1
                className="font-noto-serif text-neu-primary whitespace-nowrap"
                style={{
                  // Shrink to fit the card width in one line at all target
                  // viewports (320–440px); the Korean full-date string
                  // "2026년 9월 29일 화요일" is the widest case.
                  fontSize: "clamp(1.15rem, 5.4vw, 1.65rem)",
                  lineHeight: 1.15,
                  marginTop: 12,
                  marginBottom: 22,
                }}
              >
                {fullDateLabel}
              </h1>
            )}

            {currentPassageDate && (
              <div className="relative grid grid-cols-[48px_1fr_48px] items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    currentDay?.previous_date &&
                    navigate(`/devotions/date/${currentDay.previous_date}`)
                  }
                  disabled={!currentDay?.previous_date}
                  aria-label="Previous day"
                  className="w-12 h-12 rounded-full bg-canvas-base text-neu-primary flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-[1px] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-cta focus-visible:outline-offset-2"
                  style={{ boxShadow: MOBILE_BTN_SHADOW }}
                >
                  <FiChevronLeft aria-hidden="true" size={20} />
                </button>

                <button
                  ref={mobileCalendarBtnRef}
                  type="button"
                  onClick={() => setMobileCalendarOpen((v) => !v)}
                  aria-label={`Choose date, currently ${fullDateLabel}`}
                  aria-haspopup="dialog"
                  aria-expanded={mobileCalendarOpen}
                  className="min-h-[48px] w-full px-4 rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold flex items-center justify-center gap-2 active:translate-y-[1px] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-cta focus-visible:outline-offset-2"
                  style={{ boxShadow: MOBILE_BTN_SHADOW }}
                >
                  <FiCalendar aria-hidden="true" className="text-accent-cta shrink-0" />
                  <span className="truncate">{shortDateLabel}</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    currentDay?.next_date &&
                    navigate(`/devotions/date/${currentDay.next_date}`)
                  }
                  disabled={!currentDay?.next_date}
                  aria-label="Next day"
                  className="w-12 h-12 rounded-full bg-canvas-base text-neu-primary flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-[1px] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-cta focus-visible:outline-offset-2"
                  style={{ boxShadow: MOBILE_BTN_SHADOW }}
                >
                  <FiChevronRight aria-hidden="true" size={20} />
                </button>

                <DevotionCalendarPopover
                  open={mobileCalendarOpen}
                  onClose={() => {
                    setMobileCalendarOpen(false);
                    mobileCalendarBtnRef.current?.focus();
                  }}
                  onSelect={chooseFromMobileCalendar}
                  anchorRef={mobileCalendarBtnRef}
                  date={currentPassageDate}
                  language={language}
                  className="absolute left-1/2 -translate-x-1/2 top-full z-40 mt-2 w-[min(92vw,360px)]"
                />
              </div>
            )}

            {user && (
              <Link
                to="/devotions/sunday"
                className="mt-5 inline-flex items-center justify-center gap-2 min-h-[50px] w-full rounded-full bg-accent-cta text-white text-label-md font-semibold hover:bg-accent-cta-hover active:translate-y-[1px] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                style={{ boxShadow: MOBILE_PRIMARY_SHADOW }}
              >
                <FiUsers aria-hidden="true" />
                Prepare for Sunday
              </Link>
            )}
          </section>

          {/* ------------------------------------------------------------------
              Header — DESKTOP / TABLET (>600px), unchanged behavior.
             ------------------------------------------------------------------ */}
          <section
            className={`hidden min-[601px]:flex rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex-col gap-space-md sm:flex-row sm:items-center sm:justify-between`}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Daily Devotion
              </span>
              {currentPassageDate && (
                <CompactDatePicker date={currentPassageDate} language={language} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-space-sm">
              {currentDay?.previous_date && (
                <button
                  type="button"
                  onClick={() => navigate(`/devotions/date/${currentDay.previous_date}`)}
                  className={navPill}
                >
                  <FiChevronLeft aria-hidden="true" />
                  Previous
                </button>
              )}
              {currentDay?.next_date && (
                <button
                  type="button"
                  onClick={() => navigate(`/devotions/date/${currentDay.next_date}`)}
                  className={navPill}
                >
                  Next
                  <FiChevronRight aria-hidden="true" />
                </button>
              )}
              {user && (
                <Link
                  to="/devotions/sunday"
                  className="inline-flex items-center gap-space-xs h-11 px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_12px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all"
                >
                  <FiUsers aria-hidden="true" />
                  Prepare for Sunday
                </Link>
              )}
            </div>
          </section>

          {loading && (
            <section
              className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-lg text-slate-muted`}
            >
              Loading today’s passage…
            </section>
          )}
          {error && (
            <section
              className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-lg text-[#b02a37]`}
            >
              {error}
            </section>
          )}

          {currentDay && (
            <>
              {/* ------------------------------------------------------------
                  Mobile / tablet (<lg): two-tab switch between the passage
                  and the Pastor's Guide + Reflection form so the page stops
                  being one very long scroll.
                 ------------------------------------------------------------ */}
              <div className="lg:hidden flex flex-col gap-space-md">
                <div
                  role="tablist"
                  aria-label="Devotion sections"
                  className={`inline-flex p-1 rounded-full bg-canvas-base ${PAGE_RAISED_SM} self-stretch`}
                >
                  <button
                    role="tab"
                    aria-selected={mobileTab === "passage"}
                    type="button"
                    onClick={() => setMobileTab("passage")}
                    className={`flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] px-space-md rounded-full text-label-md font-semibold transition-all ${
                      mobileTab === "passage"
                        ? "bg-accent-cta text-white"
                        : "text-neu-primary hover:bg-white/50"
                    }`}
                  >
                    <FiBookOpen aria-hidden="true" />
                    Passage
                  </button>
                  <button
                    role="tab"
                    aria-selected={mobileTab === "reflect"}
                    type="button"
                    onClick={() => setMobileTab("reflect")}
                    className={`flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] px-space-md rounded-full text-label-md font-semibold transition-all ${
                      mobileTab === "reflect"
                        ? "bg-accent-cta text-white"
                        : "text-neu-primary hover:bg-white/50"
                    }`}
                  >
                    <FiEdit3 aria-hidden="true" />
                    Reflect
                  </button>
                </div>

                {mobileTab === "passage" ? (
                  <PassageReader
                    passage={currentDay.passage}
                    verses={currentDay.verses}
                    version={version}
                    onVersionChange={changeVersion}
                    user={user}
                    onRequireLogin={() => openModal(<LoginForm />)}
                  />
                ) : (
                  <div className="flex flex-col gap-space-md">
                    <PastoralGuide content={currentDay.pastoral_content} />
                    <DevotionEditor
                      passage={currentDay.passage}
                      entry={currentDay.entry}
                      user={user}
                    />
                  </div>
                )}
              </div>

              {/* ------------------------------------------------------------
                  Desktop (≥lg): user picks between a two-column read-and-
                  write layout and a single-column stack. Choice persists in
                  localStorage. No inner scrollbars — both columns grow with
                  their content.
                 ------------------------------------------------------------ */}
              <div className="hidden lg:flex lg:flex-col gap-space-md">
                <div className="flex items-center justify-end">
                  <div
                    role="group"
                    aria-label="Choose layout"
                    className={`inline-flex p-1 rounded-full bg-canvas-base ${PAGE_RAISED_SM}`}
                  >
                    <button
                      type="button"
                      aria-pressed={desktopLayout === "split"}
                      onClick={() => updateDesktopLayout("split")}
                      className={`inline-flex items-center gap-1.5 h-9 px-space-sm rounded-full text-label-md font-semibold transition-all ${
                        desktopLayout === "split"
                          ? "bg-accent-cta text-white"
                          : "text-neu-primary hover:bg-white/50"
                      }`}
                    >
                      <FiColumns aria-hidden="true" />
                      Two columns
                    </button>
                    <button
                      type="button"
                      aria-pressed={desktopLayout === "stack"}
                      onClick={() => updateDesktopLayout("stack")}
                      className={`inline-flex items-center gap-1.5 h-9 px-space-sm rounded-full text-label-md font-semibold transition-all ${
                        desktopLayout === "stack"
                          ? "bg-accent-cta text-white"
                          : "text-neu-primary hover:bg-white/50"
                      }`}
                    >
                      <FiAlignJustify aria-hidden="true" />
                      Single column
                    </button>
                  </div>
                </div>

                {desktopLayout === "split" ? (
                  <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-gutter items-start">
                    <div className="min-w-0">
                      <PassageReader
                        passage={currentDay.passage}
                        verses={currentDay.verses}
                        version={version}
                        onVersionChange={changeVersion}
                        user={user}
                        onRequireLogin={() => openModal(<LoginForm />)}
                      />
                    </div>
                    <div className="flex flex-col gap-space-md min-w-0">
                      <PastoralGuide content={currentDay.pastoral_content} />
                      <DevotionEditor
                        passage={currentDay.passage}
                        entry={currentDay.entry}
                        user={user}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-space-md">
                    <PassageReader
                      passage={currentDay.passage}
                      verses={currentDay.verses}
                      version={version}
                      onVersionChange={changeVersion}
                      user={user}
                      onRequireLogin={() => openModal(<LoginForm />)}
                    />
                    <PastoralGuide content={currentDay.pastoral_content} />
                    <DevotionEditor
                      passage={currentDay.passage}
                      entry={currentDay.entry}
                      user={user}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DailyDevotionPage;
